// ProfileDropdown.tsx
import { useState } from 'react';
import { Transition, Popover, PopoverButton, PopoverPanel } from '@headlessui/react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';
import Skeleton from 'react-loading-skeleton';
import { createPortal } from 'react-dom';

import Typography from '~/shared/components/Typography';
import { useAuthContext } from '~/shared/context/useAuthContext';

import UserAvatar from './UserAvatar';
import UserProfile from './UserProfile';
import LogOutModal from './LogoutModal';
import AccountSwitcher from './AccountSwitcher';

export default function ProfileDropdown() {
  const [showLogOutModal, setShowLogOutModal] = useState<boolean>(false);
  const { user, activeTenantName } = useAuthContext();

  // Portal container for the dropdown content
  const portalContainer = document.body;

  return (
    <Popover as="div" className="relative z-10">
      {({ open }: { open: boolean }) => (
        <>
          <PopoverButton className="w-full group outline-hidden profile-trigger">
            <div className="flex gap-[14px] items-center">
              <div className="flex items-center rounded-full bg-gray-200 p-1 cursor-pointer">
                <UserAvatar username={user?.name || ''} size="responsive" />
                <div className="px-[13px] flex">
                  {activeTenantName ? (
                    <Typography variant="p3-resp" fontWeight={600}>
                      {activeTenantName}
                    </Typography>
                  ) : (
                    <Skeleton
                      containerClassName="h-6 block"
                      className="rounded-[8px]"
                      baseColor="#E2E6EC"
                      highlightColor="#FFFF"
                      width={60}
                    />
                  )}
                </div>
              </div>
              <ChevronDownIcon
                className={clsx(
                  'w-[16px] h-[16px] text-gray stroke-3 cursor-pointer transition-transform duration-200 ease-in-out',
                  open ? 'rotate-180' : ''
                )}
              />
            </div>
          </PopoverButton>

          {/* Use createPortal with proper exit animation */}
          {createPortal(
            <Transition
              show={open}
              enter="transition ease-out duration-300"
              enterFrom="transform opacity-0 scale-95 translate-y-[-8px]"
              enterTo="transform opacity-100 scale-100 translate-y-0"
              leave="transition ease-in duration-200"
              leaveFrom="transform opacity-100 scale-100 translate-y-0"
              leaveTo="transform opacity-0 scale-95 translate-y-[-8px]"
              beforeEnter={() => {
                // Update position before entering
                const element = document.querySelector('[data-dropdown-panel]') as HTMLElement;
                if (element) {
                  element.style.setProperty('--dropdown-right', getDropdownRightPosition());
                  element.style.setProperty('--dropdown-top', getDropdownTopPosition());
                }
              }}
            >
              <div
                data-dropdown-panel
                className="z-50 fixed right-[var(--dropdown-right,auto)] top-[var(--dropdown-top,auto)] flex flex-col py-[14px] px-[17px] mt-[10px] rounded-[12px] gap-4 bg-white w-[423px] shadow-modal"
                style={
                  {
                    '--dropdown-right': getDropdownRightPosition(),
                    '--dropdown-top': getDropdownTopPosition(),
                  } as React.CSSProperties
                }
              >
                <PopoverPanel static className="w-full  flex flex-col gap-4">
                  {({ close }) => (
                    <>
                      {/* Overlay to handle closing the dropdown */}
                      <div
                        className="fixed inset-0 bg-transparent z-[-1]"
                        onClick={() => close()}
                      />

                      <UserProfile
                        userName={user?.name || ''}
                        email={user?.email || ''}
                        onLogoutClick={() => setShowLogOutModal(true)}
                      />

                      {/* switch account */}
                      <AccountSwitcher />
                    </>
                  )}
                </PopoverPanel>
              </div>
            </Transition>,
            document.body // The portal container
          )}

          {createPortal(
            <LogOutModal
              showModal={showLogOutModal}
              onClose={() => setShowLogOutModal(false)}
              username={user?.name || ''}
            />,
            portalContainer
          )}
        </>
      )}
    </Popover>
  );
}

// Helper functions to calculate dropdown position
function getDropdownRightPosition() {
  // Calculate position based on the trigger element
  const triggerElement = document.querySelector('.profile-trigger');
  if (triggerElement) {
    const rect = triggerElement.getBoundingClientRect();
    return `${window.innerWidth - rect.right}px`;
  }
  return '32px'; // Fallback position
}

function getDropdownTopPosition() {
  // Calculate position based on the trigger element
  const triggerElement = document.querySelector('.profile-trigger');
  if (triggerElement) {
    const rect = triggerElement.getBoundingClientRect();
    return `${rect.bottom}px`;
  }
  return '32px'; // Fallback position
}
